import React, {useEffect} from 'react';
import {Box, Container, Typography} from '@mui/material';
import {useKeycloak} from '@react-keycloak/web';
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAxios} from "../../utils/hooks";
import {
    API_ARTIFACT_DOWNLOAD_ENDPOINT,
    API_BASE_URL,
    API_RESOURCE_DOWNLOAD_ENDPOINT
} from "../../utils/constants";

interface ContentDownloadPageProps {
}

const extractFilename = (contentDisposition?: string): string => {
    if (!contentDisposition) return "report.pdf";

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const traditionalMatch = contentDisposition.match(/filename="(.+)"/);
    if (traditionalMatch?.[1]) return decodeURIComponent(traditionalMatch[1]);

    return "report.pdf";
};

const downloadFile = (data: any, contentType: string, filename: string) => {
    const blob = new Blob([data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
};

const getDownloadEndpoint = (pathname: string) => {
    if (pathname === "/artifact/content") {
        return API_ARTIFACT_DOWNLOAD_ENDPOINT;
    }

    return API_RESOURCE_DOWNLOAD_ENDPOINT;
};

const hasMeaningfulText = (value?: string | null): value is string =>
    typeof value === "string" && value.trim().length > 0;

const isJsonContentType = (contentType?: string) =>
    !!contentType && (contentType.includes("application/json") || contentType.includes("application/problem+json"));

const parseJsonSafely = (value: string) => {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
};

const extractFailMessageFromDetail = (detail?: string): string | undefined => {
    if (!hasMeaningfulText(detail)) {
        return undefined;
    }

    const directMatch = detail.match(/"failMessage":"([^"]+)"/);
    const directFailMessage = directMatch?.[1];
    if (hasMeaningfulText(directFailMessage)) {
        return directFailMessage.trim();
    }

    const firstBrace = detail.indexOf("{");
    const lastBrace = detail.lastIndexOf("}");
    if (firstBrace < 0 || lastBrace <= firstBrace) {
        return undefined;
    }

    const embeddedJson = detail.slice(firstBrace, lastBrace + 1).replace(/\\"/g, "\"");
    const parsed = parseJsonSafely(embeddedJson);
    return hasMeaningfulText(parsed?.failMessage) ? parsed.failMessage.trim() : undefined;
};

const extractProblemFailMessage = async (error: any): Promise<string | undefined> => {
    const contentType = error?.response?.headers?.["content-type"];
    if (!isJsonContentType(contentType)) {
        return undefined;
    }

    const responseData = error?.response?.data;
    if (!responseData) {
        return undefined;
    }

    if (typeof responseData === "object" && !(responseData instanceof Blob)) {
        if (hasMeaningfulText(responseData?.failMessage)) {
            return responseData.failMessage.trim();
        }

        return extractFailMessageFromDetail(responseData?.detail);
    }

    const responseText =
        responseData instanceof Blob ? await responseData.text() : String(responseData);
    const parsed = parseJsonSafely(responseText);
    if (!parsed) {
        return undefined;
    }

    if (hasMeaningfulText(parsed?.failMessage)) {
        return parsed.failMessage.trim();
    }

    return extractFailMessageFromDetail(parsed?.detail);
};

const ContentDownloadPage: React.FC<ContentDownloadPageProps> = () => {
    const { keycloak } = useKeycloak();
    const location = useLocation();
    const navigate = useNavigate();
    const axios = useAxios(API_BASE_URL ?? "", keycloak);
    const [message, setMessage] = React.useState<string | undefined>(undefined);

    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid');
    const downloadEndpoint = getDownloadEndpoint(location.pathname);

    useEffect(() => {
        if (!uuid) {
            alert("Invalid download parameters.");
            return navigate("/");
        }

        setMessage("Downloading content...");

        (async () => {
            try {
                const response = await axios.current?.get(
                    downloadEndpoint,
                    {
                        params: { uuid: uuid},
                        responseType: "blob",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    },
                );

                const contentType = response?.headers["content-type"] || "";
                if (response?.status === 202 || isJsonContentType(contentType)) {
                    const text = await response?.data.text();
                    const json = parseJsonSafely(text);
                    setMessage(
                        (hasMeaningfulText(json?.failMessage) ? json.failMessage.trim() : extractFailMessageFromDetail(json?.detail))
                        || "Artifact is still being generated, please try again later."
                    );
                    return;
                }

                const filename = extractFilename(response?.headers["content-disposition"]);

                downloadFile(
                    response?.data,
                    response?.headers["content-type"],
                    filename,
                );
            } catch (error: any) {
                console.error(error);
                const failMessage = await extractProblemFailMessage(error);
                if (failMessage) {
                    setMessage(failMessage);
                    return;
                }

                setMessage("Failed to download content." + error?.message);
            }
        })();
    }, [axios, downloadEndpoint, navigate, uuid]);

    return (
        <Container maxWidth="lg">
            <Box sx={{mt: 4, mb: 4}}>
                <Typography variant="h4" component="h1" gutterBottom>
                    { message ?? "Preparing to download content..." }
                </Typography>
            </Box>
        </Container>
    );
};

export default ContentDownloadPage;
